/**
 * UYAP oturumunun kime ait olduğunun denetimi.
 *
 * Bu bir GÜVENLİK SINIRIDIR. Görevler hesaba bağlıdır; bir avukatın UYAP
 * oturumuyla başkasının işlemleri yapılmamalıdır. Aksi halde bir kullanıcı
 * hesabını başkasına verip, kendi e-imzasıyla açılmış oturum üzerinden
 * işlem yaptırabilir.
 *
 * Üç kimlik de tutmalı: hesap TC == kart TC == oturum TC.
 *
 * Plugin içinde duruyor (uygulamaya özel değil): hem masaüstü imzalayıcı hem
 * icrapro-app aynı UYAP oturumunu paylaşıyor, denetim ikisinde de aynı olmalı.
 * Saf tutulur (store/DOM bağımlılığı yok) ki birim testten geçebilsin.
 */

/** Karşılaştırma için yalnızca rakamları alır. Bilinmeyen değer boş string döner. */
export function tcknNormalize(deger: any): string {
    return String(deger ?? '').replace(/\D/g, '');
}

export interface KimlikDenetimSonucu {
    uyusuyor: boolean;
    /** Uyuşmuyorsa hangi kimliğin çeliştiği — loglama için. */
    sebep?: 'hesap' | 'kart';
}

/**
 * Oturum, hesap ve takılı kart aynı kişiye mi ait?
 *
 * Bilinmeyen (boş) değerler karşılaştırmaya girmez: kimliği doğrulayamamak,
 * uyuşmazlık kanıtı değildir. Bu yüzden eksik veri bağlantıyı düşürmez.
 */
export function oturumKimligiUyusuyor(kimlikler: {
    oturum?: any;
    hesap?: any;
    kart?: any;
}): KimlikDenetimSonucu {
    const oturum = tcknNormalize(kimlikler.oturum);
    if (!oturum) {
        return {uyusuyor: true};
    }

    const hesap = tcknNormalize(kimlikler.hesap);
    if (hesap && hesap !== oturum) {
        return {uyusuyor: false, sebep: 'hesap'};
    }

    const kart = tcknNormalize(kimlikler.kart);
    if (kart && kart !== oturum) {
        return {uyusuyor: false, sebep: 'kart'};
    }

    return {uyusuyor: true};
}

/**
 * Hesabın TC'sine ait kartı seçer.
 *
 * Kart TCKN'si (certificate.subjectSerial) hesap TCKN'siyle eşleşen kart seçilir.
 * Eskiden kart seçimi körlemesine liste[0] idi ve TCKN yalnızca giriş anında
 * REDDETMEK için okunuyordu; çoklu kartta doğru kart elde olmasına rağmen
 * "İzinsiz İmza" alınıyordu.
 *
 * Eşleşme yoksa: tek kart varsa belirsizlik de yok, o seçilir. Birden fazla kart
 * varken rastgele seçmek başka bir avukatın kartıyla giriş demek — null döner ve
 * seçim kullanıcıya bırakılır.
 */
export function hesabaAitKartiSec(kartlar: any[], hesapTcknHam: any): string | null {
    if (!Array.isArray(kartlar) || kartlar.length === 0) {
        return null;
    }
    const hesapTckn = tcknNormalize(hesapTcknHam);
    if (hesapTckn) {
        const eslesen = kartlar.find(
            (kart: any) => tcknNormalize(kart?.certificate?.subjectSerial) === hesapTckn
        );
        if (eslesen) {
            return eslesen?.certificate?.certificateId ?? null;
        }
    }
    if (kartlar.length === 1) {
        return kartlar[0]?.certificate?.certificateId ?? null;
    }
    return null;
}

export const KIMLIK_UYUSMAZLIGI_MESAJI =
    'Açık olan UYAP oturumu bu hesaba ait değil. Hesabınızda kayıtlı TC kimlik ' +
    'numarası ile UYAP oturumundaki TC kimlik numarası farklı olduğu için işlem ' +
    'yapılamaz. Kendi hesabınızla giriş yapıp kendi e-imzanızı kullanın.';

/**
 * Kimlik uyuşmazlığı tespit edildiğinde çağrılacak kanca (engelleyici dialog +
 * botu durdurma). Kota/devre kesici kancalarıyla aynı desen: uygulama kaydeder,
 * kaydedilmezse sessizce atlanır — böylece bu modül store/DOM'a bağlanmaz.
 */
let kimlikUyusmazligiKancasi: ((sebep: 'hesap' | 'kart') => void) | null = null;

export function kimlikUyusmazligiKancasiniAyarla(
    fn: ((sebep: 'hesap' | 'kart') => void) | null
): void {
    kimlikUyusmazligiKancasi = fn;
}

export function kimlikUyusmazligiBildir(sebep: 'hesap' | 'kart'): void {
    if (!kimlikUyusmazligiKancasi) {
        return;
    }
    try {
        kimlikUyusmazligiKancasi(sebep);
    } catch (e) {
        console.warn('Kimlik uyuşmazlığı uyarısı gösterilemedi:', e);
    }
}
